export type AnalyticsConsent = "unknown" | "granted" | "denied";

const consentStorageKey = "pantryverse.productAnalytics.consent.v1";

interface ConsentStorage {
  getItem(key: string): string | null;
  setItem(key: string, value: string): void;
}

const parseConsent = (value: string | null): AnalyticsConsent =>
  value === "denied" ? "denied" : "granted";

export class AnalyticsConsentStore {
  private consent: AnalyticsConsent;
  private storageAvailable = true;
  private readonly listeners = new Set<(consent: AnalyticsConsent) => void>();

  constructor(private readonly storage: ConsentStorage) {
    try {
      this.consent = parseConsent(storage.getItem(consentStorageKey));
    } catch {
      // Product Analytics is an opt-out feature. Storage failures must not
      // silently turn the product-wide default into an opt-in flow.
      this.consent = "granted";
      this.storageAvailable = false;
    }
  }

  get current(): AnalyticsConsent {
    if (this.storageAvailable) {
      try {
        const storedConsent = parseConsent(
          this.storage.getItem(consentStorageKey),
        );
        if (storedConsent !== this.consent) {
          this.consent = storedConsent;
          this.listeners.forEach((listener) => listener(storedConsent));
        }
      } catch {
        // Preserve the last known decision when storage becomes unavailable.
        this.storageAvailable = false;
      }
    }
    return this.consent;
  }

  set(consent: Exclude<AnalyticsConsent, "unknown">): void {
    this.consent = consent;
    try {
      this.storage.setItem(consentStorageKey, consent);
    } catch {
      // A browser can block storage; the in-memory decision still applies.
      this.storageAvailable = false;
    }
    this.listeners.forEach((listener) => listener(consent));
  }

  subscribe(listener: (consent: AnalyticsConsent) => void): () => void {
    this.listeners.add(listener);
    return () => this.listeners.delete(listener);
  }
}

const unavailableBrowserStorage: ConsentStorage = {
  getItem: () => {
    throw new Error("Browser storage unavailable");
  },
  setItem: () => {
    throw new Error("Browser storage unavailable");
  },
};

/** Keeps browser storage acquisition outside route-critical module evaluation. */
export const createBrowserAnalyticsConsentStore = (
  loadStorage: () => ConsentStorage,
): AnalyticsConsentStore => {
  try {
    return new AnalyticsConsentStore(loadStorage());
  } catch {
    return new AnalyticsConsentStore(unavailableBrowserStorage);
  }
};
